/**
 * @file kbd.tsx
 *
 * @changelog
 * v1.2.0
 * - Added `KbdGrouper` component to handle multiple shortcuts
 * v1.1.0
 * - Sorted classnames
 * v1.0.0
 * - Added with shadcn CLI (before we used shadcn!)
 * - Swapped `cva` for `tv`
 */
import { Fragment } from 'react/jsx-runtime'
import type { ShortcutKeysInput } from '../util/shortcuts'
import { normalizeShortcuts } from '../util/shortcuts'

import { cn } from 'tailwind-variants'

function Kbd({ className, ...props }: React.ComponentProps<'kbd'>) {
  return (
    <kbd
      data-slot="kbd"
      className={cn(
        'inline-flex px-1 w-fit min-w-5 items-center justify-center gap-1 rounded-sm',
        'text-xs tracking-wide',
        'bg-white/10 text-neutral-400',
        'pointer-events-none select-none',
        className,
      )}
      {...props}
    />
  )
}

function KbdGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <kbd
      data-slot="kbd-group"
      className={cn('inline-flex items-center gap-1', className)}
      {...props}
    />
  )
}

/**
 * Custom component that builds on shadcn Kbd component.
 */
function KbdGrouper({
  keys,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  keys: ShortcutKeysInput
}) {
  return (
    <span
      className={cn('inline-flex items-center gap-1 select-none', className)}
      {...props}
    >
      {normalizeShortcuts(keys).map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && (
            <span className="text-neutral-600 font-sans lowercase text-[9px]">
              or
            </span>
          )}

          <Kbd>{group.displayText}</Kbd>
        </Fragment>
      ))}
    </span>
  )
}

export { Kbd, KbdGroup, KbdGrouper }
